import React, {
    useState,
    useMemo,
    useEffect,
    useCallback,
    useRef,
} from "react";
import Head from "next/head";
import Link from "next/link";
import { WEBPAGE_TITLE } from "@utils/Constants";
import { useNews, useNewsCategories } from "@hooks/useNews";
import TryFreeFooter from "@components/TryFreeFooter/TryFreeFooter";
import LaufidLoader from "@components/LaufidLoader/LaufidLoader";
import { useInView } from "@hooks/useInView";
import styles from "./NewsPage.module.scss";

const monthNamesIs = [
    "janúar",
    "febrúar",
    "mars",
    "apríl",
    "maí",
    "júní",
    "júlí",
    "ágúst",
    "september",
    "október",
    "nóvember",
    "desember",
];

export const helperFormatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate()}. ${monthNamesIs[d.getMonth()]} ${d.getFullYear()}`;
};

function stripHtml(html) {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").slice(0, 160) + "…";
}

const NEWS_PLACEHOLDER = "/img/laufid-logo.svg";

function getImageStyle(image) {
    if (image) return { backgroundImage: `url(${image})` };
    return {
        backgroundImage: `url(${NEWS_PLACEHOLDER})`,
        backgroundSize: "30%",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundColor: "#e8f5ee",
    };
}

function getCategoryColor(item) {
    const CATEGORY_COLORS = {
        1: "#4285F4",
        2: "#009448",
        3: "#FF6B35",
        4: "#E91E63",
        5: "#009448",
    };
    return CATEGORY_COLORS[item?.news_category_id] || "#009448";
}

const AUTOSCROLL_INTERVAL = 5000;

function HeroSlider({ articles }) {
    const [current, setCurrent] = useState(0);
    const [paused, setPaused] = useState(false);
    const timerRef = useRef(null);
    const count = articles.length;

    const goTo = useCallback(
        (idx) => {
            setCurrent(((idx % count) + count) % count);
        },
        [count],
    );

    const next = useCallback(() => goTo(current + 1), [current, goTo]);
    const prev = useCallback(() => goTo(current - 1), [current, goTo]);

    useEffect(() => {
        if (paused || count <= 1) return;
        timerRef.current = setInterval(next, AUTOSCROLL_INTERVAL);
        return () => clearInterval(timerRef.current);
    }, [paused, current, count, next]);

    return (
        <div
            className={styles.heroSlider}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div
                className={styles.heroSliderTrack}
                style={{ transform: `translateX(-${current * 100}%)` }}
            >
                {articles.map((item) => (
                    <Link
                        key={item.id}
                        href={`/news/${item.id}`}
                        className={styles.heroSlide}
                    >
                        <div
                            className={styles.heroSlideImage}
                            style={{ backgroundImage: `url(${item.image})` }}
                        />
                        <div className={styles.heroSlideOverlay} />
                        <div className={styles.heroSlideContent}>
                            <span className={styles.heroSlideBadge}>
                                {item.category?.name || "Fréttir"}
                            </span>
                            <h2 className={styles.heroSlideTitle}>
                                {item.title}
                            </h2>
                            <p className={styles.heroSlideExcerpt}>
                                {stripHtml(item.description)}
                            </p>
                            <div className={styles.heroSlideMeta}>
                                Laufið &middot; {helperFormatDate(item.date)}
                            </div>
                        </div>
                    </Link>
                ))}
            </div>

            <button
                className={`${styles.heroSliderArrow} ${styles.heroSliderArrowLeft}`}
                onClick={(e) => {
                    e.preventDefault();
                    prev();
                }}
                aria-label="Fyrri frétt"
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <polyline points="15 18 9 12 15 6" />
                </svg>
            </button>
            <button
                className={`${styles.heroSliderArrow} ${styles.heroSliderArrowRight}`}
                onClick={(e) => {
                    e.preventDefault();
                    next();
                }}
                aria-label="Næsta frétt"
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <polyline points="9 6 15 12 9 18" />
                </svg>
            </button>

            <div className={styles.heroSliderDots}>
                {articles.map((_, i) => (
                    <button
                        key={i}
                        className={`${styles.heroSliderDot} ${i === current ? styles.heroSliderDotActive : ""}`}
                        onClick={(e) => {
                            e.preventDefault();
                            goTo(i);
                        }}
                        aria-label={`Frétt ${i + 1}`}
                    />
                ))}
            </div>
        </div>
    );
}

export default function NewsPage() {
    const [activeCategoryId, setActiveCategoryId] = useState(null);
    const { data: news } = useNews(activeCategoryId);
    const { data: categoriesData } = useNewsCategories();
    const articles = (news?.results || []).filter((a) => a.image);
    const categories = categoriesData?.results || [];

    const heroArticles = useMemo(() => {
        if (activeCategoryId || articles.length < 4) return [];
        return articles.slice(0, 4);
    }, [articles, activeCategoryId]);

    const heroIds = useMemo(
        () => new Set(heroArticles.map((a) => a.id)),
        [heroArticles],
    );

    const showHero = heroArticles.length >= 4;

    const latest = activeCategoryId
        ? articles
        : articles.filter((a) => !heroIds.has(a.id));

    const [headerRef, headerInView] = useInView();
    const [gridRef, gridInView] = useInView();
    const [ctaRef, ctaInView] = useInView();

    return (
        <>
            {!news && <LaufidLoader />}
            <Head>
                <title key="site_title">{"Fréttir - " + WEBPAGE_TITLE}</title>
                <meta name="keywords" content="Fréttir Laufsins" />
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} - Fréttir Laufsins`}
                    key="og:title"
                />
                <meta name="description" content="Fréttir Laufsins" />
                <meta
                    name="og:description"
                    content="Fréttir Laufsins"
                    key="og:description"
                />
            </Head>
            <div className={styles.pageWrapper}>
                {showHero && <HeroSlider articles={heroArticles} />}

                <div
                    ref={headerRef}
                    className={`${styles.sectionHeader} ${styles.revealTarget} ${headerInView ? styles.revealed : ""}`}
                >
                    <h2 className={styles.sectionTitle}>Nýjustu fréttir</h2>
                    {categories.length > 0 && (
                        <div className={styles.sectionFilter}>
                            <button
                                className={`${styles.sectionFilterTab} ${activeCategoryId === null ? styles.sectionFilterTabActive : ""}`}
                                onClick={() => setActiveCategoryId(null)}
                            >
                                Allar
                            </button>
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    className={`${styles.sectionFilterTab} ${activeCategoryId === cat.id ? styles.sectionFilterTabActive : ""}`}
                                    onClick={() => setActiveCategoryId(cat.id)}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div
                    ref={gridRef}
                    className={`${styles.articleGrid} ${styles.revealTarget} ${gridInView ? styles.revealed : ""}`}
                >
                    {latest.length === 0 && (
                        <p className={styles.emptyState}>
                            Engar fréttir í þessum flokki.
                        </p>
                    )}
                    {latest.map((item) => (
                        <Link
                            key={item.id}
                            href={`/news/${item.id}`}
                            className={styles.articleCard}
                        >
                            <div
                                className={styles.articleCardLine}
                                style={{ background: getCategoryColor(item) }}
                            />
                            <div className={styles.articleCardImageWrap}>
                                <div
                                    className={styles.articleCardImage}
                                    style={getImageStyle(item.image)}
                                />
                                {item.category && (
                                    <span
                                        className={styles.articleCardBadge}
                                        style={{
                                            background: getCategoryColor(item),
                                        }}
                                    >
                                        {item.category.name}
                                    </span>
                                )}
                            </div>
                            <div className={styles.articleCardBody}>
                                <h3 className={styles.articleCardTitle}>
                                    {item.title}
                                </h3>
                                <div className={styles.articleCardMeta}>
                                    <span className={styles.detailMetaAuthor}>
                                        Laufið
                                    </span>
                                    <span
                                        className={styles.articleCardMetaDot}
                                    />
                                    <span>{helperFormatDate(item.date)}</span>
                                </div>
                                <p className={styles.articleCardExcerpt}>
                                    {stripHtml(item.description)}
                                </p>
                            </div>
                        </Link>
                    ))}
                </div>

                <div
                    ref={ctaRef}
                    className={`${styles.revealTarget} ${ctaInView ? styles.revealed : ""}`}
                    style={{ width: "100%", maxWidth: 1100 }}
                >
                    <TryFreeFooter
                        title="Viltu vera hluti af lausninni?"
                        subtitle="Skráðu fyrirtækið þitt á Laufið og byrjaðu sjálfbærnivegferðina í dag."
                        buttonText="Byrja ókeypis →"
                    />
                </div>
            </div>
        </>
    );
}
